import React from 'react';
import { Lightbulb } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import AlarmDot from '../../components/shared/AlarmDot';
import StatusPill from '../../components/shared/StatusPill';
import SectionDivider from '../../components/shared/SectionDivider';
import { consumerAlarms } from '../../data/mockData';
import { AlarmSeverity } from '../../types';
import { formatDateTime } from '../../utils/dateHelpers';

export default function ConsumerAlarms() {
  const activeAlarm = consumerAlarms.find(a => a.status === 'ACTIVE');
  const resolvedAlarms = consumerAlarms.filter(a => a.status === 'RESOLVED');

  return (
    <div className="p-6 space-y-6">
      {/* KPIs */}
      <div className="flex gap-4">
        <KPICard title="Active Alarms" value="1" bgColor="bg-red-50" subtextColor="red" subtext="Needs attention" />
        <KPICard title="Resolved (30 days)" value="4" subtextColor="green" subtext="All resolved" bgColor="bg-green-50" />
        <KPICard title="Last Alarm" value="25 Apr 2026" subtext="MPPT 2 degradation" />
        <KPICard title="Comms Failures" value="2" subtext="Last 30 days" />
      </div>

      {/* Active alarm */}
      {activeAlarm && (
        <div className="bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Active Alarms" />
          <div className="flex gap-3 p-3 rounded-input border border-orange-200 bg-orange-50">
            <AlarmDot severity={AlarmSeverity.WARNING} />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-semibold text-text-primary leading-tight">{activeAlarm.description}</p>
                <StatusPill status="warning" />
              </div>
              <p className="text-xs text-text-secondary mt-1">
                Parameter: <span className="font-mono">{activeAlarm.parameter}</span>
                {' · '}Duration: {activeAlarm.duration}
              </p>
              <button className="mt-2 flex items-center gap-1 text-xs border border-warning text-warning px-2 py-1 rounded-input hover:bg-orange-50 transition-colors">
                <Lightbulb size={11} /> Get advice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alarm history table */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <SectionDivider title="Alarm History" subtitle="Last 6 alarm events" />
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2.5 text-text-tertiary font-medium">Date & Time</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Fault Description</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Parameter</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Duration</th>
                <th className="text-center py-2.5 text-text-tertiary font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {consumerAlarms.map(alarm => (
                <tr key={alarm.id} className="border-b border-border/50 hover:bg-bg-secondary cursor-pointer">
                  <td className="py-2.5 text-text-secondary whitespace-nowrap">{formatDateTime(alarm.timestamp)}</td>
                  <td className="py-2.5 text-text-primary max-w-xs">{alarm.description}</td>
                  <td className="py-2.5 font-mono text-text-secondary">{alarm.parameter}</td>
                  <td className="py-2.5 text-text-secondary whitespace-nowrap">{alarm.duration}</td>
                  <td className="py-2.5 text-center">
                    {alarm.status === 'ACTIVE'
                      ? <StatusPill status="warning" label="Active" />
                      : <StatusPill status="resolved" label="Resolved" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
